import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Plus, Pause, Play, Pencil, MessageCircle, Mail, Smartphone } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge } from "./ui";
import PartnerEditor from "./PartnerEditor";

const ChannelDots = ({ ch, testid }) => (
  <span className="inline-flex items-center gap-1.5" data-testid={testid} title="WhatsApp · Email · SMS">
    {[["whatsapp", MessageCircle], ["email", Mail], ["sms", Smartphone]].map(([k, Icon]) => (
      <Icon key={k} size={13} className={ch?.[k] ? "text-success" : "text-muted/40"} />
    ))}
  </span>
);

const PartnerRow = ({ p, cityId, onPrimary, onPause, onEdit }) => (
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 rounded-sm ${p.is_primary ? "bg-brass/10" : "hover:bg-plaster-deep/60"}`}
       data-testid={`partner-row-${p.id}`}>
    <div className="w-full sm:flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/admin/florists/${p.id}`} className="text-sm text-ink font-medium underline decoration-brass underline-offset-2 truncate"
              data-testid={`partner-name-${p.id}`}>{p.shop_name}</Link>
        {p.is_primary ? <Badge tone="brass"><span className="inline-flex items-center gap-1"><Crown size={10} /> Primary</span></Badge>
                      : <Badge tone="ink">Secondary · rank {p.rank}</Badge>}
        {p.paused ? <Badge tone="warn">paused</Badge> : null}
        {p.city_id !== cityId ? <span className="text-[11px] text-muted">from {p.city}</span> : null}
      </div>
      <p className="text-[11px] text-muted truncate">{p.owner_name} · {p.whatsapp || p.phone || "—"} · {p.email || "no e-mail"} · {p.products} bouquets</p>
    </div>
    <ChannelDots ch={p.notify_channels} testid={`partner-channels-${p.id}`} />
    <div className="flex items-center gap-1 flex-wrap">
      {!p.is_primary && !p.paused ? (
        <button className="text-[11px] border border-brass px-2 py-1 rounded-sm text-brass hover:bg-brass hover:text-plaster transition-colors" data-testid={`partner-make-primary-${cityId}-${p.id}`}
                onClick={() => onPrimary(p, cityId)}>Make primary</button>
      ) : null}
      <button className="text-[11px] border border-brass-light px-2 py-1 rounded-sm hover:bg-plaster-deep inline-flex items-center gap-1 text-ink" data-testid={`partner-pause-${p.id}`}
              onClick={() => onPause(p)}>{p.paused ? <><Play size={11} /> Resume</> : <><Pause size={11} /> Pause</>}</button>
      <button className="text-[11px] border border-brass-light px-2 py-1 rounded-sm hover:bg-plaster-deep inline-flex items-center gap-1 text-ink" data-testid={`partner-edit-${p.id}`}
              onClick={() => onEdit(p)}><Pencil size={11} /> Edit</button>
    </div>
  </div>
);

export default function PartnersPage() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [editor, setEditor] = useState(null); // null | {mode:"create"} | {mode:"edit", partner}
  const [onlyStaffed, setOnlyStaffed] = useState(false);

  const load = () => api.get("/admin/partners").then((r) => setData(r.data)).catch((e) => setMsg(errText(e)));
  useEffect(() => { load(); }, []);

  const act = async (fn, done) => {
    setMsg("");
    try { await fn(); setMsg(done); load(); } catch (e) { setMsg(errText(e)); }
  };
  const onPrimary = (p, cityId) => act(() => api.post(`/admin/partners/${p.id}/primary`, { city_id: cityId }), `${p.shop_name} is now primary.`);
  const onPause = (p) => act(() => api.put(`/admin/partners/${p.id}`, { paused: !p.paused }), p.paused ? "Partner resumed." : "Partner paused — it receives no orders.");

  const cities = (data?.cities || []).filter((c) => !onlyStaffed || c.count > 0);

  return (
    <div data-testid="admin-partners-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl text-ink mb-1">Partner network</h1>
          <p className="text-sm text-muted">One hand-picked primary florist per city, secondaries ranked below. Paused partners never receive orders.</p>
        </div>
        <button className="btn-primary text-xs px-4 inline-flex items-center gap-1.5" data-testid="partner-add-button"
                onClick={() => setEditor({ mode: "create" })}><Plus size={14} /> Add partner florist</button>
      </div>
      {msg ? <p className="text-sm text-brass mb-3" data-testid="partners-message">{msg}</p> : null}
      <label className="text-xs text-muted inline-flex items-center gap-2 mb-3">
        <input type="checkbox" checked={onlyStaffed} onChange={(e) => setOnlyStaffed(e.target.checked)} data-testid="partners-only-staffed" />
        Hide cities without a partner
      </label>

      {!data ? <p className="text-sm text-muted">Loading…</p> : null}
      <div className="grid gap-3 lg:grid-cols-2">
        {cities.map((c) => (
          <section key={c.city_id} className="card p-3 min-w-0" data-testid={`partner-city-${c.city_id}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-lg text-ink">{c.city}</h2>
              <span className="text-[11px] text-muted">{c.count} partner{c.count === 1 ? "" : "s"}</span>
            </div>
            {c.primary ? <PartnerRow p={c.primary} cityId={c.city_id} onPrimary={onPrimary} onPause={onPause} onEdit={(p) => setEditor({ mode: "edit", partner: p })} />
                       : <p className="text-xs text-warning px-3 py-2 border border-warning/40 bg-warning/10 rounded-sm" data-testid={`no-primary-${c.city_id}`}>No primary florist — orders here will escalate to operations.</p>}
            {c.secondaries.length ? (
              <div className="mt-1 space-y-px">
                {c.secondaries.map((p) => <PartnerRow key={p.id} p={p} cityId={c.city_id} onPrimary={onPrimary} onPause={onPause} onEdit={(p2) => setEditor({ mode: "edit", partner: p2 })} />)}
              </div>
            ) : <p className="text-[11px] text-muted px-3 pt-2">No secondary available.</p>}
          </section>
        ))}
      </div>

      {editor ? (
        <PartnerEditor mode={editor.mode} partner={editor.partner} cities={data?.city_options || []}
                       onClose={() => setEditor(null)} onSaved={(m) => { setEditor(null); setMsg(m); load(); }} />
      ) : null}
    </div>
  );
}
